import { create } from 'zustand';
import { establishmentsAPI, usersAPI, roomsAPI, subscriptionsAPI } from '../services/api';

const useAdminStore = create((set, get) => ({
  // State
  platformStats: null,
  establishments: [],
  isLoadingStats: false,
  isLoadingEstablishments: false,
  error: null,

  // Fetch platform statistics
  fetchPlatformStats: async () => {
    set({ isLoadingStats: true, error: null });
    try {
      // Get all data in parallel
      const [establishments, users, rooms, subscriptions] = await Promise.all([
        establishmentsAPI.getAll(),
        usersAPI.getAll(),
        roomsAPI.getAll(),
        subscriptionsAPI.getAllSubscriptions()
      ]);

      // Calculate statistics
      // Filter only active and trial subscriptions for counts
      const activeSubscriptions = subscriptions?.filter(s =>
        s.status === 'active' || s.status === 'trial'
      ) || [];

      const stats = {
        total_establishments: establishments?.length || 0,
        total_users: users?.length || 0,
        total_rooms: rooms?.length || 0,
        subscription_basic: activeSubscriptions.filter(s => s.plan === 'basic').length || 0,
        subscription_professional: activeSubscriptions.filter(s => s.plan === 'professional').length || 0,
        subscription_enterprise: activeSubscriptions.filter(s => s.plan === 'enterprise').length || 0,
        active_establishments: establishments?.filter(e => e.is_active).length || 0,
        active_subscriptions: subscriptions?.filter(s => s.status === 'active').length || 0,
        trial_subscriptions: subscriptions?.filter(s => s.status === 'trial').length || 0,
      };

      set({
        platformStats: stats,
        isLoadingStats: false
      });

      return stats;
    } catch (error) {
      console.error('Error fetching platform stats:', error);
      set({
        error: 'Error al cargar las estadísticas de la plataforma',
        isLoadingStats: false
      });
      throw error;
    }
  },

  // Fetch establishments
  fetchEstablishments: async () => {
    set({ isLoadingEstablishments: true, error: null });
    try {
      const establishmentsData = await establishmentsAPI.getAll();
      set({
        establishments: establishmentsData,
        isLoadingEstablishments: false
      });
      return establishmentsData;
    } catch (error) {
      console.error('Error fetching establishments:', error);
      set({
        error: 'Error al cargar los establecimientos',
        isLoadingEstablishments: false
      });
      throw error;
    }
  },

  // Create establishment
  createEstablishment: async (establishmentData) => {
    try {
      const newEstablishment = await establishmentsAPI.create(establishmentData);
      set(state => ({
        establishments: [...state.establishments, newEstablishment]
      }));
      // Refresh stats after creating
      get().fetchPlatformStats();
      return newEstablishment;
    } catch (error) {
      console.error('Error creating establishment:', error);
      throw error;
    }
  },

  // Update establishment
  updateEstablishment: async (id, establishmentData) => {
    try {
      const updatedEstablishment = await establishmentsAPI.update(id, establishmentData);
      set(state => ({
        establishments: state.establishments.map(e =>
          e.id === id ? updatedEstablishment : e
        )
      }));
      return updatedEstablishment;
    } catch (error) {
      console.error('Error updating establishment:', error);
      throw error;
    }
  },

  // Delete establishment
  deleteEstablishment: async (id) => {
    try {
      await establishmentsAPI.delete(id);
      set(state => ({
        establishments: state.establishments.filter(e => e.id !== id)
      }));
      // Refresh stats after deleting
      get().fetchPlatformStats();
    } catch (error) {
      console.error('Error deleting establishment:', error);
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Reset store
  reset: () => set({
    platformStats: null,
    establishments: [],
    isLoadingStats: false,
    isLoadingEstablishments: false,
    error: null
  })
}));

export default useAdminStore;
